/**
 * COMPTABILITÉ — ÉCARTER UN RAPPORT, OU PLUSIEURS.
 *
 * Pendant un entretien ou une modification de caisse, ZAP émet des rapports
 * qui ne décrivent aucune nuit réelle (une soirée de test côté Shotgun aussi).
 * On les ÉCARTE, avec un motif : ils ne comptent plus nulle part, restent
 * visibles, barrés, et se rétablissent d'un geste. Rien n'est supprimé — un
 * document source est une preuve.
 *
 * Deux gestes, un seul serveur :
 *   · `Ecarter` — un rapport, en ligne, là où il est lu ;
 *   · `BarreEcart` — la sélection : une matinée d'entretien produit plusieurs
 *     rapports, ils s'écartent ensemble avec UN motif (tout ou rien, et
 *     chaque rapport garde son audit côté serveur).
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Ban, RotateCcw, X } from 'lucide-react'
import { Modal, adminMutate, useToast } from '../admin/primitives.js'
import { MotifField, motifSuffisant } from './ClosingCommon.js'
import './finance-nights.css'

/** Le plafond du serveur (`ECART_LOT_MAX`) : au-delà, ce n'est plus un entretien. */
export const ECART_LOT_MAX = 50

/** Une sélection d'identifiants, sans bibliothèque : un Set immuable. */
export function useSelectionDocs() {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set())
  return {
    ids,
    a: (id: string) => ids.has(id),
    basculer: (id: string) => setIds((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id); else n.add(id)
      return n
    }),
    poser: (liste: string[], on: boolean) => setIds((s) => {
      const n = new Set(s)
      for (const id of liste) { if (on) n.add(id); else n.delete(id) }
      return n
    }),
    vider: () => setIds(new Set()),
  }
}

async function envoyer(ids: string[], ignored: boolean, motif: string) {
  return ids.length === 1
    ? adminMutate<{ error?: string; message?: string; documents?: unknown[] }>(
      'POST', `/api/v1/finance/sources/${ids[0]}/ignore`, { ignored, reason: motif.trim() })
    : adminMutate<{ error?: string; message?: string; documents?: unknown[] }>(
      'POST', '/api/v1/finance/sources/ignore', { document_ids: ids, ignored, reason: motif.trim() })
}

function bilan(ignored: boolean, choisis: number, touches: number): string {
  const pieces = touches > choisis ? `, avec ${touches - choisis} pièce${touches - choisis > 1 ? 's' : ''} des mêmes courriels` : ''
  if (choisis === 1) {
    return ignored
      ? `Rapport écarté${pieces} : il ne compte plus dans aucune nuit.`
      : 'Rapport rétabli : il compte de nouveau dans sa nuit.'
  }
  return ignored
    ? `${choisis} rapports écartés${pieces} : ils ne comptent plus dans aucune nuit.`
    : `${choisis} rapports rétablis : ils comptent de nouveau dans leur nuit.`
}

/** Écarter (ou rétablir) UN rapport, en ligne. */
export function Ecarter(props: { documentId: string; retablir?: boolean; onChange: () => void }) {
  const toast = useToast()
  const [ouvert, setOuvert] = useState(false)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const valider = async () => {
    setEnvoi(true)
    const r = await envoyer([props.documentId], !props.retablir, motif)
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', bilan(!props.retablir, 1, Array.isArray(r.data.documents) ? r.data.documents.length : 1))
      setOuvert(false)
      setMotif('')
      props.onChange()
      return
    }
    toast('error', r.data.message ?? 'L’opération n’a pas abouti.')
  }

  if (!ouvert) {
    return (
      <div className="fn-rapport__actions">
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => setOuvert(true)}>
          {props.retablir ? <RotateCcw size={14} aria-hidden="true" /> : <Ban size={14} aria-hidden="true" />}
          {props.retablir ? 'Rétablir' : 'Écarter'}
        </button>
      </div>
    )
  }
  return (
    <div className="fn-ecart-form">
      <MotifField
        id={`motif-${props.documentId}`}
        valeur={motif}
        onChange={setMotif}
        label={props.retablir ? 'Pourquoi le rétablir ?' : 'Pourquoi l’écarter ? (par exemple « entretien d’une caisse du bar »)'}
        aide="Ce motif reste attaché au rapport, pour toujours."
      />
      <div className="fn-rapport__actions">
        <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={() => { setOuvert(false); setMotif('') }}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary lc-btn--sm" disabled={!motifSuffisant(motif) || envoi} onClick={() => void valider()}>
          {envoi ? 'Envoi en cours' : props.retablir ? 'Rétablir le rapport' : 'Écarter le rapport'}
        </button>
      </div>
    </div>
  )
}

/**
 * La barre de la SÉLECTION — en bas d'écran, comme partout dans Gestion
 * (`lc-bulkbar`). Elle propose « Écarter » s'il y a un rapport retenu dans la
 * sélection, « Rétablir » s'il y a un rapport écarté ; le motif est demandé
 * une fois, pour tout le lot.
 */
export function BarreEcart(props: {
  /** Les documents choisis, avec leur état actuel. */
  choisis: Array<{ id: string; ecarte: boolean }>
  onVider: () => void
  onFait: () => void
}) {
  const toast = useToast()
  const [geste, setGeste] = useState<'ecarter' | 'retablir' | null>(null)
  const [motif, setMotif] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const n = props.choisis.length
  const aEcarter = props.choisis.filter((d) => !d.ecarte).map((d) => d.id)
  const aRetablir = props.choisis.filter((d) => d.ecarte).map((d) => d.id)
  const cibles = geste === 'retablir' ? aRetablir : aEcarter
  const trop = n > ECART_LOT_MAX

  const fermer = () => { setGeste(null); setMotif('') }
  const valider = async () => {
    if (geste === null || cibles.length === 0) return
    setEnvoi(true)
    const r = await envoyer(cibles, geste === 'ecarter', motif)
    setEnvoi(false)
    if (r.status >= 200 && r.status < 300) {
      toast('ok', bilan(geste === 'ecarter', cibles.length, Array.isArray(r.data.documents) ? r.data.documents.length : cibles.length))
      fermer()
      props.onVider()
      props.onFait()
      return
    }
    toast('error', r.data.message ?? 'L’opération n’a pas abouti : rien n’a été modifié.')
  }

  if (n === 0) return null
  return createPortal(
    <>
      <div className="lc-bulkbar-zone">
        <div className="lc-bulkbar" role="toolbar" aria-label="Actions sur les rapports sélectionnés">
          <span className="lc-bulkbar__count">
            <span className="lc-dot" /> {n} rapport{n > 1 ? 's' : ''} sélectionné{n > 1 ? 's' : ''}
          </span>
          <span className="lc-bulkbar__actions">
            {trop ? (
              <span className="lc-hint">{ECART_LOT_MAX} au plus par lot.</span>
            ) : null}
            {aEcarter.length > 0 ? (
              <button type="button" className="lc-btn" disabled={trop} onClick={() => setGeste('ecarter')}>
                <Ban size={14} aria-hidden="true" /> Écarter{aRetablir.length > 0 ? ` (${aEcarter.length})` : ''}
              </button>
            ) : null}
            {aRetablir.length > 0 ? (
              <button type="button" className="lc-btn" disabled={trop} onClick={() => setGeste('retablir')}>
                <RotateCcw size={14} aria-hidden="true" /> Rétablir{aEcarter.length > 0 ? ` (${aRetablir.length})` : ''}
              </button>
            ) : null}
          </span>
          <button type="button" className="lc-btn lc-btn--ghost lc-bulkbar__clear" onClick={props.onVider} aria-label="Vider la sélection">
            <X size={15} />
          </button>
        </div>
      </div>

      <Modal open={geste !== null} onClose={fermer} owning width={540}
        ariaLabel={geste === 'retablir' ? 'Rétablir les rapports sélectionnés' : 'Écarter les rapports sélectionnés'}>
        <header className="lc-modal__head">
          <span className="lc-modal__icon" aria-hidden="true">
            {geste === 'retablir' ? <RotateCcw size={22} /> : <Ban size={22} />}
          </span>
          <div>
            <div className="lc-modal__title">
              {geste === 'retablir'
                ? `Rétablir ${cibles.length} rapport${cibles.length > 1 ? 's' : ''}`
                : `Écarter ${cibles.length} rapport${cibles.length > 1 ? 's' : ''}`}
            </div>
          </div>
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={fermer} aria-label="Fermer">
            <X size={18} />
          </button>
        </header>
        <div className="lc-modal__body fiw-modal">
          <p className="lc-hint">
            {geste === 'retablir'
              ? 'Ils compteront de nouveau dans leur nuit. L’écart reste dans l’historique.'
              : 'Ils ne compteront plus dans aucune nuit, aucun total ni préremplissage — mais restent visibles, barrés, avec ce motif. Les pièces des mêmes courriels suivent.'}
          </p>
          <MotifField
            id="fn-motif-lot"
            valeur={motif}
            onChange={setMotif}
            label={geste === 'retablir' ? 'Pourquoi les rétablir ?' : 'Pourquoi les écarter ? (par exemple « entretien des caisses du bar »)'}
            aide="Ce motif reste attaché à chaque rapport, pour toujours."
          />
          <div className="fiw-modal__actions">
            <button type="button" className="lc-btn lc-btn--ghost" onClick={fermer}>Annuler</button>
            <button type="button" className="lc-btn lc-btn--primary" disabled={!motifSuffisant(motif) || envoi || cibles.length === 0}
              onClick={() => void valider()}>
              {envoi ? 'Envoi en cours' : geste === 'retablir' ? 'Rétablir' : 'Écarter'}
            </button>
          </div>
        </div>
      </Modal>
    </>,
    document.body,
  )
}
